import classNames from 'classnames';
import React from 'react';
import { useI18n, LocalizedHtml } from '@udemy/i18n';
import { Icon } from '@udemy/icons';
import { Button, Image } from '@udemy/react-core-components';
import { BlockSkeleton, TextSkeleton, ImageSkeleton } from '@udemy/react-reveal-components';
import { useFormatCurrency } from '@udemy/ud-data';
import { PlanPeriod } from '../plan-period/plan-period.react-component';
import styles from "./personal-plan-banner.module.css";
const PersonalPlanBannerItemSkeleton = () => /*#__PURE__*/React.createElement("li", {
  className: styles['feature-item']
}, /*#__PURE__*/React.createElement(BlockSkeleton, {
  className: styles['feature-item-icon-skeleton']
}), /*#__PURE__*/React.createElement("div", {
  className: styles['feature-item-text-wrapper']
}, /*#__PURE__*/React.createElement(BlockSkeleton, {
  className: styles['feature-item-text-skeleton']
})));
const CtaSkeleton = () => /*#__PURE__*/React.createElement("div", {
  className: styles['cta-section'],
  "data-testid": "cta-skeleton"
}, /*#__PURE__*/React.createElement(BlockSkeleton, {
  className: styles['cta-button-skeleton']
}), /*#__PURE__*/React.createElement(TextSkeleton, {
  lineCountPerParagraph: 1,
  className: styles['price-text-skeleton']
}));
const ImageSkeletonWrapper = () => /*#__PURE__*/React.createElement("div", {
  className: styles['image-skeleton'],
  "data-testid": "image-skeleton"
}, /*#__PURE__*/React.createElement(ImageSkeleton, null));
const getDefaultFeatures = gettext => [{
  glyph: 'sparkles',
  text: gettext('<span class="firstWord">Learn</span> AI and more'),
  bgColor: 'color-purple-150'
}, {
  glyph: 'trophy',
  text: gettext('<span class="firstWord">Prep</span> for a certification'),
  bgColor: 'color-green-150'
}, {
  glyph: 'question-answer-outline',
  text: gettext('<span class="firstWord">Practice</span> with AI coaching'),
  bgColor: 'color-orange-150'
}, {
  glyph: 'lightbulb',
  text: gettext('<span class="firstWord">Advance</span> your career'),
  bgColor: 'color-teal-150'
}];
const PersonalPlanBannerItem = ({
  glyph,
  text = '',
  bgColor
}) => /*#__PURE__*/React.createElement("li", {
  className: styles['feature-item']
}, /*#__PURE__*/React.createElement("div", {
  className: styles['feature-icon'],
  style: {
    backgroundColor: `var(--${bgColor})`
  }
}, /*#__PURE__*/React.createElement(Icon, {
  glyph: glyph,
  label: false
})), /*#__PURE__*/React.createElement(LocalizedHtml, {
  className: "ud-text-md",
  html: text,
  interpolate: {
    firstWord: /*#__PURE__*/React.createElement("strong", null)
  }
}));
const getDefaultImageProps = gettext => ({
  src: 'https://frontends.udemycdn.com/staticx/udemy/images/ai-career-banner/ai-career@1x.webp',
  srcSet: 'https://frontends.udemycdn.com/staticx/udemy/images/ai-career-banner/ai-career@1x.webp 1x, https://frontends.udemycdn.com/staticx/udemy/images/ai-career-banner/ai-career@2x.webp 2x',
  alt: gettext('Role play interface showing AI conversation practice')
});
export const PersonalPlanBanner = ({
  subscriptionPrice,
  title,
  description,
  features,
  imageProps,
  headingElement: HeadingComponent = 'h3',
  className,
  isLoading = false,
  ctaSlot,
  ctaUrl,
  onCtaClick
}) => {
  const {
    gettext,
    interpolate
  } = useI18n();
  const {
    formatCurrency
  } = useFormatCurrency();
  const formattedSubscriptionPrice = typeof subscriptionPrice === 'number' ? formatCurrency(subscriptionPrice) : '';
  return /*#__PURE__*/React.createElement("section", {
    className: classNames(styles['personal-plan-banner'], className)
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['banner-content']
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames(styles['content-section'], {
      [styles['content-section-skeleton']]: isLoading
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['text-content']
  }, isLoading ? /*#__PURE__*/React.createElement("div", {
    "data-testid": "title-skeleton"
  }, /*#__PURE__*/React.createElement(TextSkeleton, {
    withTitle: true,
    lineCountPerParagraph: 2,
    className: styles['title-skeleton']
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HeadingComponent, {
    className: classNames('ud-heading-xxl', styles['title'])
  }, title !== null && title !== void 0 ? title : gettext('Reimagine your career in the AI era')), /*#__PURE__*/React.createElement("p", {
    className: styles['description']
  }, description !== null && description !== void 0 ? description : gettext('Future-proof your skills with Personal Plan. Get access to a variety of fresh content from real-world experts.')))), /*#__PURE__*/React.createElement("ul", {
    className: classNames(styles['features-grid'], 'ud-unstyled-list')
  }, isLoading ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PersonalPlanBannerItemSkeleton, null), /*#__PURE__*/React.createElement(PersonalPlanBannerItemSkeleton, null), /*#__PURE__*/React.createElement(PersonalPlanBannerItemSkeleton, null), /*#__PURE__*/React.createElement(PersonalPlanBannerItemSkeleton, null)) : (features !== null && features !== void 0 ? features : getDefaultFeatures(gettext)).map((feature, index) => /*#__PURE__*/React.createElement(PersonalPlanBannerItem, Object.assign({
    key: index
  }, feature)))), isLoading ? /*#__PURE__*/React.createElement(CtaSkeleton, null) : ctaSlot ? ctaSlot : /*#__PURE__*/React.createElement("div", {
    className: styles['cta-section']
  }, /*#__PURE__*/React.createElement(Button, Object.assign({
    className: styles['cta-button'],
    size: "medium",
    udStyle: "white-solid",
    onClick: onCtaClick
  }, ctaUrl && {
    componentClass: 'a',
    href: ctaUrl
  }, {
    "data-testid": "cta-button"
  }), gettext('Learn more')), /*#__PURE__*/React.createElement("div", {
    className: styles['price-text']
  }, /*#__PURE__*/React.createElement(PlanPeriod, {
    listPriceText: formattedSubscriptionPrice,
    textOverride: interpolate(gettext('Starting at %(price)s/month'), {
      price: formattedSubscriptionPrice
    }, true)
  })))), /*#__PURE__*/React.createElement("div", {
    className: styles['image-section']
  }, isLoading ? /*#__PURE__*/React.createElement(ImageSkeletonWrapper, null) : /*#__PURE__*/React.createElement(Image, imageProps !== null && imageProps !== void 0 ? imageProps : getDefaultImageProps(gettext)))));
};
//# sourceMappingURL=personal-plan-banner.react-component.js.map